import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScanLine } from 'lucide-react';
import { FitTag, buildTagDataFromDetail, loadCustomMm, tagMaterials } from '@/components/jewellery/JewelleryTagViewer';
import { useLabelPresets } from '@/lib/tagPresets';

const KEY = 'tagws_fitcheck';
const SEL = 'h-8 rounded-lg border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]';

// Picks a label size and measures every visible card against it (hidden render).
export function TagFitCheck({ items, onResults }) {
  const { mine, company } = useLabelPresets();
  const presets = useMemo(() => [...company, ...mine.filter((m) => !company.some((c) => c.name === m.name))], [company, mine]);
  const [choice, setChoice] = useState(() => localStorage.getItem(KEY) || '');
  const last = loadCustomMm();
  const size = useMemo(() => {
    if (!choice) return null;
    if (choice === '__last') return { name: 'Last custom', ...last };
    const p = presets.find((x) => x.name === choice);
    return p || null;
  }, [choice, presets, last.w, last.h]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (v) => { setChoice(v); if (v) localStorage.setItem(KEY, v); else localStorage.removeItem(KEY); };
  const [res, setRes] = useState({});
  useEffect(() => { setRes({}); }, [size?.w, size?.h]);
  useEffect(() => { onResults(size ? { size, bad: res } : null); }, [res, size, onResults]);
  const report = useCallback((id, r) => setRes((m) => (m[id] === !r.fits ? m : { ...m, [id]: !r.fits })), []);

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="tagws-fit-check-bar">
      <ScanLine size={14} className="text-[hsl(var(--ring))]" />
      <span>Label fit check:</span>
      <select value={size ? choice : ''} onChange={(e) => pick(e.target.value)} className={SEL} data-testid="tagws-fit-check-select">
        <option value="">Off</option>
        {presets.map((p) => <option key={p.name} value={p.name}>{p.name} ({p.w}×{p.h} mm)</option>)}
        <option value="__last">Last custom size ({last.w}×{last.h} mm)</option>
      </select>
      {size ? (
        <span data-testid="tagws-fit-check-summary">
          {Object.values(res).filter(Boolean).length} of {items.length} on this page won&apos;t fit
        </span>
      ) : null}
      {size ? (
        <div aria-hidden style={{ position: 'fixed', left: -10000, top: 0, pointerEvents: 'none' }}>
          {items.map((it) => {
            const data = buildTagDataFromDetail(it);
            return (
              <FitTag
                key={`${it.id}-${size.w}x${size.h}-${it.tagws_layout || ''}`}
                wMm={size.w} hMm={size.h} overflow="block" forceLayout={it.tagws_layout || 'auto'}
                onFit={(r) => report(it.id, r)}
                layoutProps={{ data, materials: tagMaterials(data, 'expanded'), showNotes: true, qr: 64, qrValue: '', showQr: false, showPieces: true }}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
